import * as z from 'zod';

export const ListTippableStaffRequest = z.object({
  options: z
    .object({
      includeWixUsers: z
        .boolean()
        .describe(
          'Whether to include staff who are only Wix users and not\nBookings staff members ([SDK](https://dev.wix.com/docs/sdk/backend-modules/bookings/staff-members/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/staff-member-object))\nin the response. Setting `{"includeWixUsers": true}` returns all tippable\nstaff, including Bookings staff, site collaborators, or those who are both.\nBy default, or if you provide `{"includeWixUsers": false}`, only Bookings\nstaff members or those who are both are returned.\n\nDefault: `false`.'
        )
        .optional(),
      filterByName: z
        .string()
        .describe(
          "Filters the returned staff by name. If you provide a name as filter, only\nstaff whose names start with the given input are returned. The filter isn't\ncase sensitive.\n\nMax: 500 characters"
        )
        .max(500)
        .optional()
        .nullable(),
      filterByIds: z.array(z.string()).min(0).max(100).optional(),
    })
    .describe('Options to use when listing tippable staff.')
    .optional(),
});
export const ListTippableStaffResponse = z.object({
  staff: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'Staff ID. Matches `staffMemberId` if available, or\n`identificationData.wixUserId` if not.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe(
            'Staff name. Matches the name of the\nBookings staff member ([SDK](https://dev.wix.com/docs/sdk/backend-modules/bookings/staff-members/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/staff-member-object)),\nif available.'
          )
          .max(500)
          .optional()
          .nullable(),
        staffMemberId: z
          .string()
          .describe(
            'Staff member ID. Available only if the staff is connected to a\nBookings staff member ([SDK](https://dev.wix.com/docs/sdk/backend-modules/bookings/staff-members/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/staff-member-object)).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        identificationData: z
          .intersection(
            z.object({
              contactId: z
                .string()
                .describe(
                  "ID of the contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system.\nSee the Contacts API for additional details."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                anonymousVisitorId: z
                  .string()
                  .describe(
                    "ID of a site visitor who hasn't logged in to the site."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                memberId: z
                  .string()
                  .describe(
                    'ID of a site visitor who has logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                appId: z.never().optional(),
                wixUserId: z
                  .string()
                  .describe(
                    'ID of a Wix user. For example, the site owner or a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z
                  .string()
                  .describe('ID of an app.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            'Identification data of the staff. Available only if the staff is a\n[site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).'
          )
          .optional(),
      })
    )
    .max(100)
    .optional(),
});
